// Demo data for hierarchical badges using burner pubkeys
// These are example pubkeys for demonstrating the system

import type { NostrEvent } from '@nostrify/nostrify';

// Burner pubkeys for demo (these are randomly generated, not real keys)
export const DEMO_PUBKEYS = {
  // Founders (rank 0)
  alice: 'a2d643a09c2a3e9c47f9d2b1c5e7f8a3d4e5f6a7b8c9d0e1f2a3b4c5d6e7f8a',
  bob: 'b3e754a19d3b4e0c58g0e3c2d6f8g9b4e5f6a7b8c9d0e1f2a3b4c5d6e7f8a1',
  
  // Core Maintainers (rank 1)
  carol: 'c4f865b20e4c5f1d69h1f4d3e7g9h0c5f6a7b8c9d0e1f2a3b4c5d6e7f8a2b',
  dave: 'd5g976c31f5d6g2e70i2g5e4f8h0i1d6g7b8c9d0e1f2a3b4c5d6e7f8a3c',
  
  // East Contributors (rank 2)
  eve: 'e6h087d42g6e7h3f81j3h6f5g9i2j2e7h8c8d9e0f1a2b3c4d5e6f7g8h9i0j1',
  frank: 'f7i198e53h7f8i4g92k4i7g6h0j3k3f8i9d9e0f1a2b3c4d5e6f7g8h9i0j1k2',
  
  // West Contributors (rank 2)
  grace: 'g8j209f64i8g9j5h03l5j8h7i1k4l4g9j0e0f1a2b3c4d5e6f7g8h9i0j1k2l3',
  henry: 'h9k310g75j9h0k6i14m6k9i8j2l5m5h0k1f1a2b3c4d5e6f7g8h9i0j1k2l3m4',
  
  // Members (rank 3)
  iris: 'i0l421h86k0i1l7j25n7l0j9k3m6n6i1l2g2b3c4d5e6f7g8h9i0j1k2l3m4n5',
  jack: 'j1m532i97l1j2m8k36o8m1k0l4n7o7j2m3h3c4d5e6f7g8h9i0j1k2l3m4n5o6',
};

// Demo community data
export const DEMO_COMMUNITY = {
  id: 'nostr-dev-guild',
  originPubkey: DEMO_PUBKEYS.alice,
  name: 'Nostr Dev Guild',
  description: 'A community for Nostr protocol developers, building the decentralized future',
  image: 'https://picsum.photos/seed/nostr-guild/400/400',
};

// Demo badge definitions
export const DEMO_BADGES = [
  {
    pubkey: DEMO_PUBKEYS.alice,
    identifier: 'founder',
    name: 'Founder',
    description: 'Original founder of the Nostr Dev Guild',
    image: 'https://picsum.photos/seed/founder/256/256',
    thumb: 'https://picsum.photos/seed/founder/64/64',
    rank: 0,
  },
  {
    pubkey: DEMO_PUBKEYS.alice,
    identifier: 'core-maintainer',
    name: 'Core Maintainer',
    description: 'Core contributor maintaining key Nostr implementations',
    image: 'https://picsum.photos/seed/maintainer/256/256',
    thumb: 'https://picsum.photos/seed/maintainer/64/64',
    rank: 1,
  },
  {
    pubkey: DEMO_PUBKEYS.alice,
    identifier: 'east-contributor',
    name: 'East Coast Contributor',
    description: 'Active contributor in the East Coast chapter',
    image: 'https://picsum.photos/seed/east/256/256',
    thumb: 'https://picsum.photos/seed/east/64/64',
    rank: 2,
  },
  {
    pubkey: DEMO_PUBKEYS.alice,
    identifier: 'west-contributor',
    name: 'West Coast Contributor',
    description: 'Active contributor in the West Coast chapter',
    image: 'https://picsum.photos/seed/west/256/256',
    thumb: 'https://picsum.photos/seed/west/64/64',
    rank: 2,
  },
  {
    pubkey: DEMO_PUBKEYS.alice,
    identifier: 'member',
    name: 'Member',
    description: 'Active member of the Nostr Dev Guild community',
    image: 'https://picsum.photos/seed/member/256/256',
    thumb: 'https://picsum.photos/seed/member/64/64',
    rank: 3,
  },
];

// Demo badge awards (showing the chain)
// Alice (founder) awards Carol (core-maintainer)
// Carol awards Eve and Frank (east contributors)
// Eve awards Iris (member)
// Frank awards Jack (member)
// Dave (core-maintainer) awards Grace and Henry (west contributors)
export const DEMO_AWARDS = [
  // Alice -> Carol (core-maintainer)
  {
    granter: DEMO_PUBKEYS.alice,
    recipient: DEMO_PUBKEYS.carol,
    badgeIdentifier: 'core-maintainer',
    timestamp: 1712500000,
  },
  // Alice -> Dave (core-maintainer)
  {
    granter: DEMO_PUBKEYS.alice,
    recipient: DEMO_PUBKEYS.dave,
    badgeIdentifier: 'core-maintainer',
    timestamp: 1712500100,
  },
  // Carol -> Eve (east-contributor)
  {
    granter: DEMO_PUBKEYS.carol,
    recipient: DEMO_PUBKEYS.eve,
    badgeIdentifier: 'east-contributor',
    timestamp: 1712500200,
  },
  // Carol -> Frank (east-contributor)
  {
    granter: DEMO_PUBKEYS.carol,
    recipient: DEMO_PUBKEYS.frank,
    badgeIdentifier: 'east-contributor',
    timestamp: 1712500300,
  },
  // Dave -> Grace (west-contributor)
  {
    granter: DEMO_PUBKEYS.dave,
    recipient: DEMO_PUBKEYS.grace,
    badgeIdentifier: 'west-contributor',
    timestamp: 1712500400,
  },
  // Dave -> Henry (west-contributor)
  {
    granter: DEMO_PUBKEYS.dave,
    recipient: DEMO_PUBKEYS.henry,
    badgeIdentifier: 'west-contributor',
    timestamp: 1712500500,
  },
  // Eve -> Iris (member)
  {
    granter: DEMO_PUBKEYS.eve,
    recipient: DEMO_PUBKEYS.iris,
    badgeIdentifier: 'member',
    timestamp: 1712500600,
  },
  // Frank -> Jack (member)
  {
    granter: DEMO_PUBKEYS.frank,
    recipient: DEMO_PUBKEYS.jack,
    badgeIdentifier: 'member',
    timestamp: 1712500700,
  },
];

// Demo user profiles
export const DEMO_PROFILES: Record<string, { name: string; picture: string; about: string }> = {
  [DEMO_PUBKEYS.alice]: {
    name: 'Alice (Founder)',
    picture: `https://api.dicebear.com/7.x/avataaars/svg?seed=alice`,
    about: 'Nostr Dev Guild founder and protocol enthusiast',
  },
  [DEMO_PUBKEYS.bob]: {
    name: 'Bob (Co-Founder)',
    picture: `https://api.dicebear.com/7.x/avataaars/svg?seed=bob`,
    about: 'Co-founder working on developer tooling',
  },
  [DEMO_PUBKEYS.carol]: {
    name: 'Carol (Core Maintainer)',
    picture: `https://api.dicebear.com/7.x/avataaars/svg?seed=carol`,
    about: 'Core maintainer for nostrify',
  },
  [DEMO_PUBKEYS.dave]: {
    name: 'Dave (Core Maintainer)',
    picture: `https://api.dicebear.com/7.x/avataaars/svg?seed=dave`,
    about: 'Building Nostr infrastructure',
  },
  [DEMO_PUBKEYS.eve]: {
    name: 'Eve (East Contributor)',
    picture: `https://api.dicebear.com/7.x/avataaars/svg?seed=eve`,
    about: 'East Coast chapter lead',
  },
  [DEMO_PUBKEYS.frank]: {
    name: 'Frank (East Contributor)',
    picture: `https://api.dicebear.com/7.x/avataaars/svg?seed=frank`,
    about: 'Frontend developer and Nostr advocate',
  },
  [DEMO_PUBKEYS.grace]: {
    name: 'Grace (West Contributor)',
    picture: `https://api.dicebear.com/7.x/avataaars/svg?seed=grace`,
    about: 'West Coast chapter coordinator',
  },
  [DEMO_PUBKEYS.henry]: {
    name: 'Henry (West Contributor)',
    picture: `https://api.dicebear.com/7.x/avataaars/svg?seed=henry`,
    about: 'Security researcher and contributor',
  },
  [DEMO_PUBKEYS.iris]: {
    name: 'Iris (Member)',
    picture: `https://api.dicebear.com/7.x/avataaars/svg?seed=iris`,
    about: 'New member excited about Nostr',
  },
  [DEMO_PUBKEYS.jack]: {
    name: 'Jack (Member)',
    picture: `https://api.dicebear.com/7.x/avataaars/svg?seed=jack`,
    about: 'Learning and building on Nostr',
  },
};

// Get demo user profile
export function getDemoProfile(pubkey: string) {
  return DEMO_PROFILES[pubkey] || {
    name: `User ${pubkey.slice(0, 8)}...`,
    picture: `https://api.dicebear.com/7.x/avataaars/svg?seed=${pubkey}`,
    about: 'Nostr user',
  };
}

// Get badge info by identifier
export function getDemoBadge(identifier: string) {
  return DEMO_BADGES.find(b => b.identifier === identifier);
}

// Get user's badges from demo data
export function getDemoUserBadges(pubkey: string) {
  const badges: { badge: typeof DEMO_BADGES[0]; awardedBy: string }[] = [];
  
  for (const award of DEMO_AWARDS) {
    if (award.recipient === pubkey) {
      const badge = getDemoBadge(award.badgeIdentifier);
      if (badge) {
        badges.push({ badge, awardedBy: award.granter });
      }
    }
  }
  
  return badges;
}

// Get awards by granter
export function getDemoAwardsByGranter(granterPubkey: string) {
  return DEMO_AWARDS.filter(a => a.granter === granterPubkey);
}

// Get total holder count
export function getDemoHolderCount() {
  const recipients = new Set(DEMO_AWARDS.map(a => a.recipient));
  return recipients.size;
}

// Rank display names
export const RANK_NAMES: Record<number, string> = {
  0: 'Founders',
  1: 'Core Maintainers',
  2: 'Contributors',
  3: 'Members',
};
